import React from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { css } from '@emotion/core'
import { FileText, Loader } from 'lucide-react'
import { StepType, useFormStepContext } from '../FormStepContext'
import { CustomTooltip } from 'components/helperStyles'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import useLocaleText from 'components/useLocaleText'
import { Button } from 'components/ui'
import { AIStepType, useAILoadingContext } from 'components/TasksV2/AITaskCreation/AIFormStepContext'
import GeneratingText from 'components/TasksV2/AITaskCreation/GeneratingText'
import { PromptGeneratorInput } from 'components/TasksV2/AITaskCreation/components/PromptGeneratorStep/PromptGeneratorInput'
import { useSubtaskMenu, AI_IMAGE_BUTTON_TYPE } from '../hooks/useSubtaskMenu'
import { DEFAULT_T_RECORD_A_VIDEO, DEFAULT_T_TAKE_A_PHOTO, localeFallback } from '../utils'

// Preserve the public import path used by existing widgets.
export { DEFAULT_T_TAKE_A_PHOTO, DEFAULT_T_RECORD_A_VIDEO }

export const iconHoverEffect = css`
  display: flex;
  align-items: center;
  column-gap: 30px;

  & > * {
    transition: transform 0.3s ease, margin 0.3s ease;
  }

  & > *:hover {
    transform: scale(1.2);
  }
`

const tooltipStyles = css`
  .chakra-tooltip {
    z-index: 999999 !important;
  }
  .chakra-tooltip__content {
    background: #2d3748;
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 14px;
  }
`

export const Footer = () => {
  const { stepStack, isEdit, isVmTask, generatePdf, isPdfGenerating, sumbitStep, isMediaUploading } =
    useFormStepContext()
  const { isGenerating } = useAILoadingContext()
  const { translations, locale } = useSelector((state: RootState) => state.config)
  const theme = useSelector((state: RootState) => state.general.theme)

  const accentColor = theme?.elementsColor || '#1A56DB'
  const t_exportToPdf = useLocaleText('export_to_pdf') || 'Export to PDF'

  const t_next = useLocaleText('next')
  const t_generating = useLocaleText('ai_generating')

  const t_done = localeFallback(useLocaleText('done'), 'done', 'Done')
  const t_ai_photo = localeFallback(useLocaleText('ai_photo_subtask'), 'ai_photo_subtask', 'AI Photo')
  const currentStep = stepStack[stepStack.length - 1]

  const { buttons: buttonsToShow, addSubtask: handleAddSubtask } = useSubtaskMenu()

  // AI enhancement sub-flow: the steps are laid out around a single centered CTA (as in the AI modal),
  // so the primary action lives here rather than in the header.
  const isAiPromptStep = currentStep === AIStepType.AI_PROMPT
  const isAiSubtaskStep = currentStep === AIStepType.AI_SUBTASK

  // The helper conversation owns the whole modal: messages in the body, input here.
  if (currentStep === AIStepType.AI_PROMPT_GENERATOR) {
    return <PromptGeneratorInput />
  }

  if (isAiPromptStep || isAiSubtaskStep) {
    const isBlocked = isMediaUploading || isGenerating
    const label = isAiPromptStep ? t_next : t_done
    return (
      <Flex w="100%" justifyContent="center">
        <Button
          variant="primary"
          w="50%"
          // Height matches the subtask-icons row of step 1 (40px) so the modal does not resize
          // between steps — BaseModalV3 keeps the body fixed, the footer is what shifts it.
          h="40px"
          borderRadius="26px"
          fontSize="15px"
          fontWeight="600"
          bg={accentColor}
          isDisabled={isBlocked}
          onClick={isBlocked ? undefined : sumbitStep}
          _hover={{ bg: accentColor, opacity: 0.88, transform: 'translateY(-1px)' }}
          _active={{ bg: accentColor, transform: 'scale(0.99)' }}
          _focus={{ outline: 'none', boxShadow: 'none' }}
        >
          {isGenerating ? (
            <GeneratingText text={t_generating} color="white" fontSize="15px" fontWeight="600" />
          ) : (
            <Text>{label}</Text>
          )}
        </Button>
      </Flex>
    )
  }

  return (
    <>
      {stepStack[stepStack.length - 1] === StepType.SUBTASK_TASK_CREATION && !isEdit ? (
        <Flex css={[iconHoverEffect, tooltipStyles]} bg="transparent">
          {buttonsToShow.map(({ type, Component, label, ariaLabel }) => {
            const isAiImage = type === AI_IMAGE_BUTTON_TYPE
            // The AI entry has no backend locale key yet, so it goes through localeFallback instead
            // of the raw translations lookup (which would render the key itself).
            const t_label = isAiImage ? t_ai_photo : ((translations[locale][label] ?? label) as string)
            const isDisabled = isAiImage && isMediaUploading

            return (
              <CustomTooltip key={type} label={t_label} aria-label={ariaLabel} placement="bottom">
                <Button
                  type="button"
                  variant="icon"
                  padding="0"
                  height="auto"
                  width="auto"
                  aria-label={t_label}
                  isDisabled={isDisabled}
                  onClick={() => handleAddSubtask(type)}
                >
                  <Component cursor={isDisabled ? 'not-allowed' : 'pointer'} />
                </Button>
              </CustomTooltip>
            )
          })}
        </Flex>
      ) : stepStack[stepStack.length - 1] === StepType.TASK_SETTINGS && isVmTask ? (
        <Button
          variant="primary"
          type="button"
          height="auto"
          isDisabled={isPdfGenerating}
          onClick={generatePdf}
          _disabled={{ cursor: 'not-allowed' }}
          display="flex"
          alignItems="center"
          style={{ columnGap: '6px', cursor: isPdfGenerating ? 'not-allowed' : 'pointer' }}
          bg={accentColor}
          color="white"
          borderRadius="8px"
          px="16px"
          py="8px"
          opacity={isPdfGenerating ? 0.7 : 1}
          _hover={{ opacity: isPdfGenerating ? 0.7 : 0.85 }}
          border="none"
          outline="none"
        >
          {isPdfGenerating ? <Loader size={14} /> : <FileText size={14} />}
          <Text fontSize="13px" fontWeight="600" color="white" ml="6px">
            {t_exportToPdf}
          </Text>
        </Button>
      ) : (
        <Box padding="20px" />
      )}
    </>
  )
}
